import { customEvent, getWindow } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import { useNavigate } from '@ps/web-shared-components';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CrmZoneContext } from '@swf/components/CrmZone/CrmZone';
import type { Props as IframeProps } from '@swf/components/Iframe/Iframe';
import Iframe from '@swf/components/Iframe/Iframe';
import useEmbeddedAPI from '@swf/core/hooks/useEmbeddedAPI';
import type { Store } from '@swf/core/store';
import store, { select } from '@swf/core/store';
import { matchC2L, matchRoute, stripC2l } from '@swf/core/utils/c2l/matchRoute';

interface CRMWindow extends Window {
  stars?: { store: Store; zoneId: string };
}

interface OwnProps {
  html: string;
  crmIframeRef?: React.MutableRefObject<HTMLIFrameElement | null>;
  dynamicHeight?: boolean;
  visible?: boolean;
}

type Props = IframeProps & OwnProps;

const CrmIframe: React.FC<Props> = ({
  html,
  dynamicHeight,
  crmIframeRef,
  visible = false,
  ...rest
}) => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const zoneId = useContext(CrmZoneContext);
  const localIframeRef = useRef<HTMLIFrameElement>(null);
  const history = useHistory();
  const navigate = useNavigate();
  const { CRMClicked, CRMViewed } = useTrackers();
  const [seen, setSeen] = useState(visible);
  useEmbeddedAPI();
  const getIframe = useCallback(() => {
    if (crmIframeRef) {
      crmIframeRef.current = localIframeRef.current;
    }
    return localIframeRef.current;
  }, [crmIframeRef]);

  const adjustHeight = useCallback(() => {
    const iframe = getIframe();
    if (iframe && dynamicHeight) {
      const iframeHeight = (iframe.contentDocument as Document)?.body?.scrollHeight;
      iframe.height = iframeHeight > 0 ? `${iframeHeight}px` : '100%';
    }
  }, [dynamicHeight, getIframe]);

  const onIframeClick = useCallback(
    (event: Event) => {
      const element = event
        .composedPath()
        .filter((elem) => !!(elem as Element).getAttribute)
        .find((elem) => (elem as HTMLAnchorElement).getAttribute('href'));

      if (element) {
        const href = (element as HTMLAnchorElement).getAttribute('href') as string;
        if (href.startsWith('/') || matchC2L(href) || matchRoute(href)) {
          event.preventDefault();
        }
        if (matchC2L(href) && isEmbedded) {
          const c2lRoute = stripC2l(href);
          getWindow()?.starsweb?.app.execute(c2lRoute);
          return;
        }
        const matchedRoute = matchRoute(href) ?? (href?.startsWith('://') && matchC2L(href));
        if (matchedRoute) {
          if (matchedRoute.route.handler) {
            matchedRoute.route.handler({
              path: matchedRoute.match,
              history,
              navigate,
              tracker: CRMClicked.bind(null, zoneId, html)
            });
          }
        } else if (href.startsWith('/')) {
          CRMClicked(zoneId, html, 'navigate');
          history.push(href);
        }
      }
      const iframe = getIframe();
      if (iframe?.parentElement) {
        iframe.parentElement.click();
      }
    },
    [CRMClicked, getIframe, history, html, isEmbedded, navigate, zoneId]
  );

  const write = useCallback(() => {
    const iframe = getIframe();
    if (iframe) {
      const { contentDocument } = iframe;
      if (contentDocument) {
        contentDocument.open('text/html', 'replace');
        contentDocument.write(html);
        contentDocument.close();
      }
    }
  }, [getIframe, html]);

  useEffect(() => {
    const iframe = getIframe();
    if (iframe && seen) {
      const { contentDocument, contentWindow } = iframe;
      if (contentWindow && contentDocument) {
        (contentWindow as CRMWindow).stars = { store, zoneId };
        contentWindow.starsweb = window.starsweb;
        write();
        visible && setSeen(false);
        contentDocument.addEventListener('click', onIframeClick);
      }
      window.addEventListener('resize', adjustHeight);
    }
    return () => {
      if (iframe) {
        const { contentDocument } = iframe;
        if (contentDocument) {
          contentDocument.removeEventListener('click', onIframeClick);
        }
        window.removeEventListener('resize', adjustHeight);
      }
    };
  }, [adjustHeight, getIframe, onIframeClick, seen, visible, write, zoneId]);

  // TODO remove with RAM1 integration
  useEffect(() => {
    customEvent({ tag: 'ramrunscrm' });
    return () => customEvent({ tag: 'scrmtargeteddestroy' });
  }, []);

  const handleChange = (visible: boolean) => {
    if (visible) {
      CRMViewed(zoneId, html);
      setSeen(true);
    }
  };

  const iframe = <Iframe {...rest} iframeRef={localIframeRef} onLoad={adjustHeight} />;
  return !visible ? (
    <InView style={{ height: '100%' }} onChange={handleChange} triggerOnce>
      {iframe}
    </InView>
  ) : (
    iframe
  );
};

export default CrmIframe;
